import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { useProfileStore } from '@/stores/profileStore'
import { Loader2, AlertCircle, X } from 'lucide-react'
import { safeErrorLog } from '@drive-without-fear/core/security/gdpr'

function isExpiredLinkError(message: string): boolean {
  const m = message.toLowerCase()
  return m.includes('expired') || m.includes('invalid token') || m.includes('otp_expired')
}

export function AuthCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { initialize } = useAuthStore()
  const { fetchProfile } = useProfileStore()
  const [error, setError] = useState<string | null>(null)
  const [toastDismissed, setToastDismissed] = useState(false)

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const errorParam = searchParams.get('error')
        const errorDescription = searchParams.get('error_description')
        if (errorParam) {
          setError(errorDescription ?? errorParam)
          return
        }

        const code = searchParams.get('code')
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
          if (exchangeError) {
            safeErrorLog('Code exchange error:', exchangeError)
            setError(exchangeError.message)
            return
          }
        }

        // Detect whether this callback came from a fresh email confirmation.
        // Supabase passes type=signup either as a query param (PKCE flow) or
        // in the URL hash fragment (implicit flow with auto-detect).
        const hashParams = new URLSearchParams(window.location.hash.slice(1))
        const callbackType = searchParams.get('type') ?? hashParams.get('type')
        const isSignupConfirmation = callbackType === 'signup'

        await initialize()
        const { data: { user }, error: userError } = await supabase.auth.getUser()

        if (userError || !user) {
          setError('Failed to get user information')
          return
        }

        // First-time email verification: keep the session live (Supabase
        // already created it during /auth/v1/verify) and show an interstitial
        // "Account verified" page. The user clicks Log in to proceed; the
        // page itself handles auto-login since the session is intact.
        if (isSignupConfirmation) {
          navigate('/auth/verified', { replace: true })
          return
        }

        await fetchProfile(user.id)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data: profile } = await (supabase.from('profiles') as any)
          .select('onboarding_completed, role')
          .eq('user_id', user.id)
          .single()

        if (!profile || !profile.onboarding_completed) {
          navigate('/onboarding', { replace: true })
        } else {
          switch (profile.role) {
            case 'admin':
              navigate('/admin', { replace: true })
              break
            case 'supporter':
              navigate('/s/dashboard', { replace: true })
              break
            default:
              navigate('/u/dashboard', { replace: true })
          }
        }
      } catch (err) {
        safeErrorLog('Auth callback error:', err)
        setError('An unexpected error occurred during authentication')
      }
    }

    handleCallback()
  }, [searchParams, initialize, fetchProfile, navigate])

  const expired = error ? isExpiredLinkError(error) : false

  return (
    <div
      className="min-h-screen flex flex-col bg-[#F1ECDF] bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: 'url(/verify-bg.jpg)' }}
    >
      {/* Top-left home link */}
      <header className="px-6 py-5">
        <Link
          to="/"
          className="text-[14px] text-[#0F1C2E]/70 hover:text-[#0F1C2E] transition-colors"
        >
          ← Home
        </Link>
      </header>

      {/* Centered content */}
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-[440px] text-center">
          <img
            src="/logo-email.png"
            alt="Drive Without Fear"
            className="w-12 h-12 mx-auto mb-8"
          />
          {error ? (
            <>
              <h1 className="text-[28px] sm:text-[32px] font-semibold text-[#0F1C2E] mb-3">
                {expired ? 'Link expired' : 'Something went wrong'}
              </h1>
              <p className="text-[15px] text-[#0F1C2E]/70 mb-10">
                {expired
                  ? 'This confirmation link is no longer valid. Please sign up again to receive a new one.'
                  : "We couldn't complete your sign-in. Please try again or contact support."}
              </p>
              <Link
                to={expired ? '/auth/sign-up' : '/auth/sign-in'}
                className="inline-flex items-center justify-center w-full sm:w-auto sm:min-w-[280px] h-[52px] px-8 bg-[#0F1C2E] text-white font-semibold rounded-full hover:bg-[#1B2A4A] transition-colors"
              >
                {expired ? 'Create account' : 'Return to log in'}
              </Link>
              <p className="mt-8 text-[13px] text-[#0F1C2E]/60">
                Need help? Contact{' '}
                <a
                  href="mailto:support@drivewithoutfear.com"
                  className="underline hover:text-[#0F1C2E]"
                >
                  support@drivewithoutfear.com
                </a>
              </p>
            </>
          ) : (
            <>
              <Loader2 className="w-8 h-8 text-[#0F1C2E]/60 animate-spin mx-auto mb-6" />
              <p className="text-[15px] text-[#0F1C2E]/70">
                Completing authentication…
              </p>
            </>
          )}
        </div>
      </main>

      {/* Bottom-right toast for expired/error */}
      {error && !toastDismissed && (
        <div
          role="alert"
          className="fixed bottom-6 right-6 max-w-[360px] bg-[#0F1C2E] border border-[#C7796B]/50 rounded-xl px-4 py-3 flex items-start gap-3 shadow-[0_12px_32px_rgba(0,0,0,0.35)]"
        >
          <AlertCircle className="w-5 h-5 text-[#C7796B] flex-shrink-0 mt-0.5" />
          <p className="text-[13px] text-white/90 leading-relaxed flex-1">
            {expired
              ? 'The confirmation link has expired. Please sign up again to generate a new link.'
              : error}
          </p>
          <button
            onClick={() => setToastDismissed(true)}
            aria-label="Dismiss"
            className="text-white/50 hover:text-white transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  )
}
